import { Icon } from '@iconify/react';
import React, { useState, useEffect } from 'react';
import config from '../config';
import { auth } from '../helper/auth';
import { formatDisplayDate } from '../utils/formatDate';

const AVAILABLE_PERMISSIONS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'users', label: 'User Management' },
    { key: 'venues', label: 'Venue Management' },
    { key: 'content', label: 'Content Moderation' },
    { key: 'support', label: 'Customer Support' },
    { key: 'finance', label: 'Financial Management' },
    { key: 'settings', label: 'Settings' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'positions', label: 'Roles & Permissions' },
    { key: 'employees', label: 'Staff Management' }
];

const PositionManagementLayer = () => {
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        permissions: []
    });

    useEffect(() => {
        fetchPositions();
    }, []);

    const fetchPositions = async () => {
        setLoading(true);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/positions`);
            if (!response.ok) throw new Error('Failed to fetch positions');
            const data = await response.json();
            setPositions(data);
        } catch (error) {
            console.error('Error fetching positions:', error);
            // toast.error('Failed to load positions');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (key) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(key)
                ? prev.permissions.filter(p => p !== key)
                : [...prev.permissions, key];
            return { ...prev, permissions: newPermissions };
        });
    };

    const openAddModal = () => {
        setFormData({ title: '', description: '', permissions: [] });
        setIsEditing(false);
        setCurrentId(null);
        setShowModal(true);
    };

    const openEditModal = (position) => {
        setFormData({
            title: position.title,
            description: position.description,
            permissions: position.permissions || []
        });
        setIsEditing(true);
        setCurrentId(position.id);
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title.trim()) {
            alert('Title is required');
            return;
        }

        try {
            const url = isEditing 
                ? `${config.API_BASE_URL}/api/positions/${currentId}`
                : `${config.API_BASE_URL}/api/positions`;
            
            const method = isEditing ? 'PUT' : 'POST';

            const response = await auth.fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Operation failed');
            }

            // toast.success(isEditing ? 'Position updated' : 'Position created');
            setShowModal(false);
            fetchPositions();
        } catch (error) {
            console.error('Error saving position:', error);
            alert(error.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this position? This action cannot be undone.')) return;

        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/positions/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) throw new Error('Failed to delete position');

            // toast.success('Position deleted');
            fetchPositions();
        } catch (error) {
            console.error('Error deleting position:', error);
            alert('Failed to delete position');
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            {/* Header */}
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center gap-3">
                    <h6 className="text-lg fw-semibold mb-0">Role Management</h6>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <button 
                        onClick={openAddModal}
                        className="btn btn-primary d-flex align-items-center gap-2"
                    >
                        <Icon icon="ic:round-plus" className="text-xl" />
                        Add New Role
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="card-body p-24">
                {loading ? (
                    <div className="text-center p-5">Loading...</div>
                ) : (
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0 hs-table-fit">
                            <thead>
                                <tr>
                                    <th scope="col">Title</th>
                                    <th scope="col">Description</th>
                                    <th scope="col">Permissions</th>
                                    <th scope="col">Created At</th>
                                    <th scope="col" className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {positions.length === 0 ? (
                                    <tr>
                                        <td colspan="5" className="text-center p-4">No positions found.</td>
                                    </tr>
                                ) : positions.map(pos => (
                                    <tr key={pos.id}>
                                        <td className="fw-medium">{pos.title}</td>
                                        <td>{pos.description}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {pos.permissions && pos.permissions.length > 0 ? (
                                                    pos.permissions.map(p => {
                                                        const perm = AVAILABLE_PERMISSIONS.find(ap => ap.key === p);
                                                        return (
                                                            <span key={p} className="badge bg-primary-50 text-primary-600 border border-primary-100 px-2.5 py-1 radius-pill fw-medium text-xs">
                                                                {perm ? perm.label : p}
                                                            </span>
                                                        );
                                                    })
                                                ) : (
                                                    <span className="text-muted text-sm">No permissions</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{formatDisplayDate(pos.created_at)}</td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <button 
                                                    onClick={() => openEditModal(pos)}
                                                    className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="lucide:edit" />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(pos.id)}
                                                    className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                                >
                                                    <Icon icon="mingcute:delete-2-line" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEditing ? 'Edit Role' : 'Add New Role'}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Role Title <span className="text-danger">*</span></label>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            name="title" 
                                            value={formData.title} 
                                            onChange={handleInputChange} 
                                            placeholder="e.g. Senior Editor"
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Description</label>
                                        <textarea 
                                            className="form-control" 
                                            name="description" 
                                            value={formData.description} 
                                            onChange={handleInputChange} 
                                            placeholder="Description of this role..."
                                            rows="3"
                                        ></textarea>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold mb-2">Module Access</label>
                                        <div className="p-20 border border-neutral-200 rounded radius-8 bg-neutral-50">
                                            <div className="row g-3">
                                                {AVAILABLE_PERMISSIONS.map(perm => (
                                                    <div className="col-sm-6" key={perm.key}>
                                                        <div className="form-check d-flex align-items-center gap-2 mb-0">
                                                            <input 
                                                                className="form-check-input mt-0 border-neutral-400" 
                                                                type="checkbox" 
                                                                id={`perm-${perm.key}`} 
                                                                checked={formData.permissions.includes(perm.key)}
                                                                onChange={() => handlePermissionChange(perm.key)}
                                                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                            />
                                                            <label 
                                                                className="form-check-label flex-grow-1 text-secondary-light fw-medium" 
                                                                htmlFor={`perm-${perm.key}`}
                                                                style={{ cursor: 'pointer', marginTop: '1px' }}
                                                            >
                                                                {perm.label}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="form-text mt-2 text-secondary-light">
                                            Select the modules this role is allowed to access.
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-end gap-2 mt-4">
                                        <button type="button" className="hs-btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button type="submit" className="btn btn-primary">{isEditing ? 'Update Role' : 'Create Role'}</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PositionManagementLayer;
